import {Injectable, inject} from '@angular/core';
import {OAuthEvent, OAuthService} from 'angular-oauth2-oidc';
import {authConfig} from './auth.config';
import {Observable, of, Subject, Subscription} from 'rxjs';
import {delay} from 'rxjs/operators';
import {SessionStorageService} from '../../modules/storage/session-storage.service';
import {User} from '../../modules/base/entities/user';
import {AuthEvent, EventType, OAuthErrorType} from './auth-event';
import {OAuthErrorService} from './oauth-error.service';
import {ToastService} from '../services/toast.service';


@Injectable({
  providedIn: 'root'
})
export class AuthenticationService {
  private oauthErrorService = inject(OAuthErrorService);
  private toastService = inject(ToastService);

  constructor(private oauthService: OAuthService, private sessionStorage: SessionStorageService) {
    this.events$ = this.eventsSubject.asObservable();
    this.isAuthenticatedFlag = this.sessionStorage.getItem('user_authenticated', false);

    // Check for OAuth error parameters in URL before setting up OAuth service
    this.checkForOAuthErrors();

    this.setupOauthService();
    this.setupRefreshTimer();
    this.setupHeartbeat();
  }
  events$: Observable<AuthEvent>;
  protected eventsSubject: Subject<AuthEvent> = new Subject<AuthEvent>();
  protected oauthEventsSubscription: Subscription;
  private isAuthenticatedFlag: boolean;
  private timerSubscription: Subscription;
  private heartbeatSubscription: Subscription;
  private refreshRetryCount = 0;
  private readonly maxRefreshRetries = 3;
  private isRefreshing = false;
  private lastRefreshAttempt = 0;
  private oauthErrorRetryCount = 0;
  private readonly maxOAuthErrorRetries = 3;

  static uuidv4(): string {
    return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function (c) {
      // tslint:disable-next-line:no-bitwise triple-equals
      const r = Math.random() * 16 | 0, v = c == 'x' ? r : (r & 0x3 | 0x8);
      return v.toString(16);
    });
  }

  triggerEvent(event: AuthEvent) {
    this.eventsSubject.next(event);
  }

  connectivityChanged(online: boolean) {
    if (online) {
      this.oauthService.sessionChecksEnabled = true;
      // Check if token needs refresh - timer will be set up after refresh completes
      const expiration = this.oauthService.getAccessTokenExpiration();
      const timeout = this.calculateTimeout(expiration);
      if (timeout === 0) {
        this.refreshToken();
      } else {
        this.setupRefreshTimer();
      }
    } else {
      this.oauthService.sessionChecksEnabled = false;
      this.stopRefreshTimer();
    }
  }

  appFocusChanged(eventType: EventType) {
    if (eventType === 'visible') {
      const expiration = this.oauthService.getAccessTokenExpiration();
      const timeout = this.calculateTimeout(expiration);
      if (timeout === 0) {
        this.refreshToken();
      }
    }
  }

  getAccountUrl(): string {
    return this.oauthService.issuer + '/account';
  }

  getFullName(): string {
    const claims = this.oauthService.getIdentityClaims();
    if (!claims) {
      return null;
    }
    return claims['name'];
  }

  getEmailAddress(): string {
    const claims = this.oauthService.getIdentityClaims();
    if (!claims) {
      return null;
    }
    return claims['email'];
  }

  getFirstName(): string {
    return this.getUserName();
  }

  getUserName(): string {
    const claims = this.oauthService.getIdentityClaims();
    if (!claims) {
      return null;
    }
    return claims['given_name'];
  }

  getSurname(): string {
    const claims = this.oauthService.getIdentityClaims();
    if (!claims) {
      return null;
    }
    return claims['family_name'];
  }

  getUserId() {
    const claims = this.oauthService.getIdentityClaims();
    if (!claims) {
      return null;
    }
    return claims['sub'];
  }

  getUser(): User {
    return {
      email: this.getEmailAddress(),
      firstName: this.getUserName(),
      surname: this.getSurname(),
      userId: this.getUserId(),
      name: this.getFullName()
    };
  }

  isAuthenticated(): boolean {
    return this.isAuthenticatedFlag;
  }

  login() {
    this.oauthService.initLoginFlow();
  }

  logout() {
    this.oauthService.logOut();
  }

  refreshToken() {
    // Prevent duplicate refresh attempts
    if (this.isRefreshing) {
      console.warn('Token refresh already in progress');
      return;
    }

    // Throttle refresh attempts (minimum 1 second between attempts)
    const now = Date.now();
    if (now - this.lastRefreshAttempt < 1000) {
      console.warn('Token refresh throttled - too soon after last attempt');
      return;
    }

    if (this.oauthService.tokenEndpoint === null) {
      console.error('Token endpoint not found - cannot refresh token');
      this.scheduleRefreshRetry();
      return;
    }

    if (this.oauthService.getRefreshToken() === null) {
      console.error('Refresh token not found - user may need to re-authenticate');
      // Don't retry if refresh token is missing - this requires re-login
      this.handleLogout();
      return;
    }

    this.isRefreshing = true;
    this.lastRefreshAttempt = now;

    this.oauthService.refreshToken()
      .then(() => {
        console.log('Token refresh successful');
        this.refreshRetryCount = 0;
        this.isRefreshing = false;
        // Timer will be set up by handleLogin() when token_received event fires
      })
      .catch((error) => {
        console.error('Token refresh failed:', error);
        this.isRefreshing = false;
        this.scheduleRefreshRetry();
      });
  }

  private setupOauthService() {
    this.oauthEventsSubscription = this.oauthService.events.subscribe((event) => this.oauthEventsHandler(event));
    this.oauthService.configure(authConfig);
    this.oauthService.loadDiscoveryDocumentAndLogin().catch(error => {
      console.error(error);
      this.handleLogout();
    });
  }

  private setupRefreshTimer() {
    this.stopRefreshTimer();
    const expiration = this.oauthService.getAccessTokenExpiration();
    const timeout = this.calculateTimeout(expiration);
    this.timerSubscription = of(new AuthEvent('token_expired')).pipe(delay(timeout)).subscribe(() => {
      this.refreshToken();
    });
  }

  private stopRefreshTimer() {
    if (this.timerSubscription) {
      this.timerSubscription.unsubscribe();
    }
  }

  private handleLogout() {
    this.isAuthenticatedFlag = false;
    this.sessionStorage.setItem('user_authenticated', this.isAuthenticatedFlag);
    this.stopRefreshTimer();
    this.stopHeartbeat();
    this.refreshRetryCount = 0;
    this.isRefreshing = false;
  }

  private handleLogin() {
    const previousState = this.isAuthenticatedFlag;
    this.setupRefreshTimer();
    this.refreshRetryCount = 0;
    this.isRefreshing = false;
    this.isAuthenticatedFlag = true;
    if (!previousState && this.isAuthenticatedFlag) {
      // delay to allow the UI to finish updating prior to emitting the event
      setTimeout(() => {
        this.eventsSubject.next(new AuthEvent('login'));
      }, 10);
      // Ensure heartbeat is running
      if (!this.heartbeatSubscription || this.heartbeatSubscription.closed) {
        this.setupHeartbeat();
      }
    }
    this.sessionStorage.setItem('user_authenticated', this.isAuthenticatedFlag);
  }

  private oauthEventsHandler(event: OAuthEvent) {
    if (
      event.type === 'logout' ||
      event.type === 'token_refresh_error' ||
      event.type === 'discovery_document_load_error' ||
      event.type === 'discovery_document_validation_error') {
      this.handleLogout();
    }
    if (event.type === 'token_received') {
      this.handleLogin();
    }
  }

  private calculateTimeout(expiration: number): number {
    const now = Date.now();
    const timeoutFactor = this.oauthService.timeoutFactor;
    const delta = (expiration - now) * timeoutFactor;
    return Math.max(0, delta);
  }

  private scheduleRefreshRetry() {
    if (this.refreshRetryCount >= this.maxRefreshRetries) {
      console.error('Max token refresh retries exceeded - logging out');
      this.handleLogout();
      return;
    }

    this.refreshRetryCount++;
    // Exponential backoff: 2s, 4s, 8s
    const retryDelay = Math.pow(2, this.refreshRetryCount) * 1000;
    console.log(`Scheduling token refresh retry ${this.refreshRetryCount}/${this.maxRefreshRetries} in ${retryDelay}ms`);

    this.stopRefreshTimer();
    this.timerSubscription = of(new AuthEvent('token_expired')).pipe(delay(retryDelay)).subscribe(() => {
      this.refreshToken();
    });
  }

  private setupHeartbeat() {
    // Check every 5 minutes that refresh timer is active when authenticated
    this.heartbeatSubscription = of(true).pipe(delay(300000)).subscribe(() => {
      if (this.isAuthenticated() && !this.timerSubscription) {
        console.warn('Refresh timer not active - reestablishing');
        this.setupRefreshTimer();
      }
      // Reschedule next heartbeat
      this.setupHeartbeat();
    });
  }

  private stopHeartbeat() {
    if (this.heartbeatSubscription) {
      this.heartbeatSubscription.unsubscribe();
    }
  }

  /**
   * Checks for OAuth error parameters in the URL and handles them appropriately
   */
  private checkForOAuthErrors() {
    const errorParams = this.oauthErrorService.extractErrorFromUrl();

    if (!errorParams) {
      return;
    }

    // Log the error for debugging
    this.oauthErrorService.logError(errorParams);

    const errorType = this.oauthErrorService.getErrorType(errorParams.error);
    const userMessage = this.oauthErrorService.getUserFriendlyMessage(errorType, errorParams.error_description);

    // Emit OAuth error event
    this.eventsSubject.next(new AuthEvent('oauth_error', { errorParams, errorType }));

    // Handle different error types
    this.handleOAuthError(errorType, userMessage, errorParams);

    // Clean URL parameters
    this.oauthErrorService.cleanUrlParameters();
  }

  /**
   * Handles OAuth errors based on error type
   */
  private handleOAuthError(errorType: OAuthErrorType, userMessage: string, errorParams: any) {
    switch (errorType) {
      case OAuthErrorType.TEMPORARILY_UNAVAILABLE:
      case OAuthErrorType.SERVER_ERROR:
        // Retriable errors - show message with retry option
        this.handleRetriableOAuthError(userMessage);
        break;

      case OAuthErrorType.AUTHENTICATION_EXPIRED:
        // Session expired - clear tokens and show message
        this.toastService.show({
          message: userMessage,
          options: { delay: 5000, classname: 'bg-warning' }
        });
        this.handleLogout();
        // Trigger login flow after a short delay
        setTimeout(() => {
          this.login();
        }, 1000);
        break;

      case OAuthErrorType.ACCESS_DENIED:
        // User denied access or access was denied by server
        this.toastService.show({
          message: userMessage,
          options: { delay: 10000, classname: 'bg-warning' }
        });
        this.handleLogout();
        break;

      case OAuthErrorType.INVALID_REQUEST:
      case OAuthErrorType.INVALID_SCOPE:
      case OAuthErrorType.UNKNOWN:
      default:
        // Non-retriable errors - show message and logout
        this.toastService.show({
          message: userMessage,
          options: { delay: 10000, classname: 'bg-danger' }
        });
        this.handleLogout();
        break;
    }
  }

  /**
   * Handles retriable OAuth errors with exponential backoff
   */
  private handleRetriableOAuthError(userMessage: string) {
    if (this.oauthErrorRetryCount >= this.maxOAuthErrorRetries) {
      this.toastService.show({
        message: 'Unable to connect to authentication service after multiple attempts. Please try again later.',
        options: { delay: 10000, classname: 'bg-danger' }
      });
      this.handleLogout();
      this.oauthErrorRetryCount = 0;
      return;
    }

    this.oauthErrorRetryCount++;
    const retryDelay = Math.pow(2, this.oauthErrorRetryCount) * 1000; // 2s, 4s, 8s

    this.toastService.show({
      message: `${userMessage} Retrying in ${retryDelay / 1000} seconds... (Attempt ${this.oauthErrorRetryCount}/${this.maxOAuthErrorRetries})`,
      options: { delay: retryDelay, classname: 'bg-info' }
    });

    // Schedule retry
    setTimeout(() => {
      console.log(`Retrying OAuth login after error (attempt ${this.oauthErrorRetryCount}/${this.maxOAuthErrorRetries})`);
      this.login();
    }, retryDelay);
  }
}

